import axios from 'axios';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, {
  Dispatch,
  ChangeEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { QueryCache, useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import clsx from 'clsx';
import { useRecoilState } from 'recoil';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import SearchIcon from '@mui/icons-material/Search';
import { useSession, xapi } from '../../hooks/session';
import { SwSnackbar } from '../commonComponent/views/SwSnackbar';
import SwLoading from '../commonComponent/spinner/SwLoading';
import { SwPagination } from '../commonComponent/tableElement/SwPagination';
import {
  encodeSearchWord,
  firstDayOfMonth,
  getCurrentStartDate,
  getDateList,
  getFirstMonthOfYear,
  getMonthText,
  lastDayOfMonth,
  timestamp2Localestring,
} from '../../common/helper';
import {
  KEY_BOOK_GROUP_LIST,
  KEY_BOOK_INFO_LIST,
  KEY_CLASS_READ_RANK_LIST,
  KEY_CLASS_WALK_RANK_LIST,
  KEY_CLASS_WRITE_RANK_LIST,
  KEY_PORTAL_READ_RANK_LIST,
  KEY_PORTAL_WALK_RANK_LIST,
  KEY_PORTAL_WRITE_RANK_LIST,
  KEY_READ_TEXT,
  KEY_WALK_TEXT,
  KEY_WRITE_TEXT,
} from '../../common/key';
import excellog from '../../images/sub/excel_icon.svg';
import {
  getBookGroupList,
  getBookInfoList,
  registerBookGroup,
} from '../../common/api/ApiBook';
import { SwBookGroup } from '../commonComponent/SwBookGroup';
import { SwBookList } from '../commonComponent/SwBookList';
import { uploadFile } from '../../common/api/ApiUpload';
import { RESULT_OK } from '../../common/resultCode';
import { SwClassStatTab } from '../commonComponent/SwClassStatTab';
import { SwUserStatDetail } from '../commonComponent/SwUserStatDetail';
import { SwSelectMonth } from '../commonComponent/SwSelectMonth';
import { SwPortalStatTab } from '../commonComponent/SwPortalStatTab';
import {
  getClassReadRankList,
  getClassWalkRankList,
  getClassWriteRankList,
} from '../../common/api/ApiClassStat';
import {
  downloadPortalRankList,
  getPortalReadRankList,
  getPortalWalkRankList,
  getPortalWriteRankList,
} from '../../common/api/ApiPortal';
import { EducationOfficeSchoolRankTable } from './EducationOfficeSchoolRankTable';
import { SwEducationOfficeStatTab } from '../commonComponent/SwEducationOfficeStatTab';
import { selectSchoolInfoState } from '../../common/atom';

const PORTAL_SCHOOL_TYPE = 'school';

interface Props {}

export const EducationOfficeSchoolRank: React.FC<Props> = () => {
  const showSelectDay = false;
  const { loginVal } = useSession();
  const [schoolClassInfo, setSchoolClassInfo] = useRecoilState(
    selectSchoolInfoState
  );
  const [educationOfficeInfo, setEducationOfficeInfo] = useState<any>();
  const [reqRequest, setReqRequest] = useState<boolean>(false);
  const [requestReadData, setRequestReadData] = useState<any>();
  const [requestWalkData, setRequestWalkData] = useState<any>();
  const [requestWriteData, setRequestWriteData] = useState<any>();
  const [loading, setLoading] = useState<any>(false);
  const [targetMonth, setTargetMonth] = useState<any>();
  const rowsPerPageOptions = [5, 10, 15, 20];
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchKey, setSearchKey] = useState<string>('book-group');
  const [searchWord, setSearchWord] = useState<string>('');
  const [dialogMessage, setDialogMessage] = useState<string>('');
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [openUserStat, setOpenUserStat] = useState<boolean>(false);

  const {
    data,
    isLoading,
    refetch: refetchPortalReadRank,
  } = useQuery(
    KEY_PORTAL_READ_RANK_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          educationOfficeSeq:
            loginVal.value.user.roleName === '마스터'
              ? 0
              : schoolClassInfo.educationOfficeSeq,
          type: PORTAL_SCHOOL_TYPE,
          month: getMonthText(targetMonth * 1000),
          page: 0,
          rowsPerPage: 0,
          searchKey: searchKey,
          searchWord: encodeSearchWord(searchKey, searchWord),
        };
        return getPortalReadRankList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestReadData(res);
        } else {
          setRequestReadData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchPortalWalkRank } = useQuery(
    KEY_PORTAL_WALK_RANK_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          educationOfficeSeq:
            loginVal.value.user.roleName === '마스터'
              ? 0
              : schoolClassInfo.educationOfficeSeq,
          type: PORTAL_SCHOOL_TYPE,
          month: getMonthText(targetMonth * 1000),
          page: 0,
          rowsPerPage: 0,
          searchKey: searchKey,
          searchWord: encodeSearchWord(searchKey, searchWord),
        };
        return getPortalWalkRankList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestWalkData(res);
        } else {
          setRequestWalkData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchPortalWriteRank } = useQuery(
    KEY_PORTAL_WRITE_RANK_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          educationOfficeSeq:
            loginVal.value.user.roleName === '마스터'
              ? 0
              : schoolClassInfo.educationOfficeSeq,
          type: PORTAL_SCHOOL_TYPE,
          month: getMonthText(targetMonth * 1000),
          page: 0,
          rowsPerPage: 0,
          searchKey: searchKey,
          searchWord: encodeSearchWord(searchKey, searchWord),
        };
        return getPortalWriteRankList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestWriteData(res);
        } else {
          setRequestWriteData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const loadSchoolStat = () => {
    refetchPortalReadRank();
    refetchPortalWalkRank();
    refetchPortalWriteRank();
  };

  const downloadPortalSchoolRank = (newType: string) => {
    const newData: any = {
      educationOfficeSeq:
        loginVal.value.user.roleName === '마스터'
          ? 0
          : schoolClassInfo.educationOfficeSeq,
      type: PORTAL_SCHOOL_TYPE,
      subType:
        newType === KEY_READ_TEXT
          ? 'read'
          : newType === KEY_WALK_TEXT
          ? 'walk'
          : 'write',
      month: getMonthText(targetMonth * 1000),
      page: 0,
      rowsPerPage: 0,
      searchKey: searchKey,
      searchWord: encodeSearchWord(searchKey, searchWord),
    };
    downloadPortalRankList(newData);
  };

  useEffect(() => {
    if (reqRequest) {
      loadSchoolStat();
    }
  }, [targetMonth]);

  useEffect(() => {
    if (reqRequest) {
      loadSchoolStat();
    }
  }, [reqRequest]);

  useEffect(() => {
    if (reqRequest) {
      loadSchoolStat();
    }
  }, [schoolClassInfo]);

  useEffect(() => {
    const newTarget = getFirstMonthOfYear(loginVal);
    setTargetMonth(newTarget);

    setRequestReadData(null);
    setReqRequest(true);
  }, []);

  return (
    <>
      <Paper sx={{ p: '1.5rem' }}>
        <SwEducationOfficeStatTab
          tabValue={1}
          educationOfficeInfo={educationOfficeInfo}
          setEducationOfficeInfo={setEducationOfficeInfo}
          showEducationOffice={false}
        />
        <SwSelectMonth
          onChangeMonth={item => {
            setTargetMonth(item);
          }}
          showSelectDay={showSelectDay}
        />
        {targetMonth && (
          <EducationOfficeSchoolRankTable
            title={KEY_READ_TEXT}
            targetMonth={targetMonth}
            data={requestReadData}
            onDownload={() => downloadPortalSchoolRank(KEY_READ_TEXT)}
          />
        )}
        <Divider sx={{ m: '', p: '' }} />
        {targetMonth && (
          <EducationOfficeSchoolRankTable
            title={KEY_WALK_TEXT}
            targetMonth={targetMonth}
            data={requestWalkData}
            onDownload={() => downloadPortalSchoolRank(KEY_WALK_TEXT)}
          />
        )}
        <Divider sx={{ m: '', p: '' }} />
        {targetMonth && (
          <EducationOfficeSchoolRankTable
            title={KEY_WRITE_TEXT}
            targetMonth={targetMonth}
            data={requestWriteData}
            onDownload={() => downloadPortalSchoolRank(KEY_WRITE_TEXT)}
          />
        )}
      </Paper>
      <SwSnackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        contents={dialogMessage}
      />
      {loading && <SwLoading />}
    </>
  );
};
